import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Mail, Building, Users, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function ContactPage() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  
  // Check if user came from partners (via state or localStorage)
  const isFromPartners = location.state?.fromPartners || localStorage.getItem("cliptour_isB2B") === "true";
  const homePath = isFromPartners ? "/partners" : "/";
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company_name: "",
    num_properties: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Message sent! We'll get back to you soon.");
        setFormData({ name: "", email: "", company_name: "", num_properties: "", message: "" });
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Header homePath={homePath} />

      <section className="section-spacing pt-32">
        <div className="container-custom">
          <motion.div 
            className="max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-12">
              <h1 className="heading-xl text-slate-900 mb-4">Get in touch</h1>
              <p className="subheading">
                Have questions about our services or need volume pricing? We'd love to hear from you.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-slate-700 font-medium mb-2 block">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-12"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-12"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company" className="text-slate-700 font-medium mb-2 block">
                    Company Name
                  </Label>
                  <Input
                    id="company"
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="h-12"
                    placeholder="Your company"
                    data-testid="contact-company-input"
                  />
                </div>
                <div>
                  <Label htmlFor="properties" className="text-slate-700 font-medium mb-2 block">
                    Number of Properties
                  </Label>
                  <Input
                    id="properties"
                    type="text"
                    value={formData.num_properties}
                    onChange={(e) => setFormData({ ...formData, num_properties: e.target.value })}
                    className="h-12"
                    placeholder="e.g., 5-10 per month"
                    data-testid="contact-properties-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-700 font-medium mb-2 block">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="min-h-[150px]"
                  placeholder="Tell us about your needs..."
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6 text-lg font-semibold"
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-12 text-center">
              <p className="text-slate-600">
                Or email us directly at{" "}
                <a href="mailto:contact@cliptour.ai" className="text-#3C59B2 font-medium hover:underline">
                  contact@cliptour.ai
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
